import type { SimEvent, FoulOutcome, PossessionWhy } from '../sim/events';
import type { TeamIdx, CardKind, MatchPhase } from '../types';
import { RESTART_KINDS } from './codecs';
import { ProtocolError } from '../errors';

// Per-type SimEvent wire codec (§9.2, MatchEventMsg 0x85). Round-trip deep-equal for every variant
// (T-PROTO-010). Sentinel 255 = null entity. Growable writer; bounds-checked reader.

const EVENT_TYPES = [
  'GOAL', 'SHOT', 'MISS', 'FOUL', 'CARD', 'DIVE_JUDGED', 'OUT_OF_PLAY', 'PHASE', 'ADVANTAGE',
  'PUNCH_LANDED', 'RAGDOLL', 'WHISTLE', 'POSSESSION', 'SAVE', 'EMOTE',
 'GK_PICKUP', 'GK_SMOTHER', 'RETURN'] as const; // D65c: RETURN appended (index-stable)
const PHASES: MatchPhase[] = ['LOBBY', 'KICKOFF', 'PLAY', 'RESTART_PENDING', 'GOAL_CELEBRATION', 'HALFTIME', 'FULLTIME'];
const RESTARTS = RESTART_KINDS; // single-sourced in ./codecs — wire order, append only
const FOUL_OUTCOMES: FoulOutcome[] = ['FREE_KICK', 'PENALTY', 'PLAY_ON', 'ADVANTAGE'];
const CARD_REASONS = ['FOUL', 'SIMULATION', 'VIOLENT', 'SECOND_YELLOW', 'REF_ABUSE'] as const;
const VERDICTS = ['AWARDED', 'PLAY_ON', 'CAUGHT'] as const;
const ADV_STATES = ['HELD', 'MATERIALIZED', 'RECALLED'] as const;
const RAGDOLL_CAUSES = ['FOUL', 'PUNCH', 'COLLISION'] as const;
const WHISTLE_KINDS = ['FOUL', 'HALF_END', 'FULL_END', 'KICKOFF', 'GOAL', 'RESTART'] as const; // D122: RESTART appended (index-stable)
// D119 — POSSESSION carries WHY the ball moved. APPEND-ONLY: the index is the wire value.
const POSSESSION_WHY: PossessionWhy[] = [
  'CLAIM', 'TACKLE_CLEAN', 'TACKLE_SPILL', 'TRAP', 'KICK',
  'KNOCK_ON', 'BODY_BLOCK', 'BARGE', 'LEASH', 'NO_TOUCH', 'SENT_OFF',
];

const idx = <T>(arr: readonly T[], v: T): number => arr.indexOf(v);
function look<T>(arr: readonly T[], i: number): T {
  const v = arr[i];
  if (v === undefined) throw new ProtocolError(`event codec: enum index ${i} out of range`);
  return v;
}
const cardIdx = (c: CardKind): number => (c === 'YELLOW' ? 0 : 1);
const cardOf = (i: number): CardKind => (i === 0 ? 'YELLOW' : 'RED');
const entOr255 = (e: number | null): number => (e === null ? 255 : e);
const entOrNull = (e: number): number | null => (e === 255 ? null : e);

export class BufWriter {
  private view = new DataView(new ArrayBuffer(64));
  private off = 0;
  private grow(n: number): void {
    if (this.off + n <= this.view.byteLength) return;
    const bigger = new Uint8Array(Math.max(this.view.byteLength * 2, this.off + n));
    bigger.set(new Uint8Array(this.view.buffer, 0, this.off));
    this.view = new DataView(bigger.buffer);
  }
  u8(v: number): void {
    this.grow(1);
    this.view.setUint8(this.off, v & 0xff);
    this.off += 1;
  }
  u32(v: number): void {
    this.grow(4);
    this.view.setUint32(this.off, v >>> 0, true);
    this.off += 4;
  }
  f64(v: number): void {
    this.grow(8);
    this.view.setFloat64(this.off, v, true);
    this.off += 8;
  }
  bytes(): Uint8Array {
    return new Uint8Array(this.view.buffer.slice(0, this.off));
  }
}

export class BufReader {
  private off = 0;
  constructor(
    private readonly view: DataView,
    private readonly len: number,
  ) {}
  private need(n: number): void {
    if (this.off + n > this.len) throw new ProtocolError('event codec: unexpected end of buffer');
  }
  u8(): number {
    this.need(1);
    return this.view.getUint8(this.off++);
  }
  u32(): number {
    this.need(4);
    const v = this.view.getUint32(this.off, true);
    this.off += 4;
    return v;
  }
  f64(): number {
    this.need(8);
    const v = this.view.getFloat64(this.off, true);
    this.off += 8;
    return v;
  }
  get atEnd(): boolean {
    return this.off === this.len;
  }
}

export function writeSimEvent(w: BufWriter, e: SimEvent): void {
  w.u8(idx(EVENT_TYPES, e.t));
  switch (e.t) {
    case 'GOAL':
      w.u8(e.team);
      w.u8(e.scorer);
      w.u8(entOr255(e.assist));
      w.u8(e.ownGoal ? 1 : 0);
      break;
    case 'SHOT':
      w.u8(e.entity);
      w.u8(e.onTarget ? 1 : 0);
      break;
    case 'MISS':
      w.u8(e.entity);
      break;
    case 'FOUL':
      w.u8(e.offender);
      w.u8(e.victim);
      w.f64(e.severity);
      w.u8(idx(FOUL_OUTCOMES, e.outcome));
      w.u8(idx(RESTARTS, e.restart));
      w.f64(e.spot.x);
      w.f64(e.spot.y);
      break;
    case 'CARD':
      w.u8(e.entity);
      w.u8(cardIdx(e.card));
      w.u8(idx(CARD_REASONS, e.reason));
      break;
    case 'DIVE_JUDGED':
      w.u8(e.entity);
      w.u8(idx(VERDICTS, e.verdict));
      break;
    case 'OUT_OF_PLAY':
      w.u8(idx(RESTARTS, e.restart));
      w.u8(e.team);
      w.f64(e.spot.x);
      w.f64(e.spot.y);
      w.u8(entOr255(e.taker)); // D122
      break;
    case 'PHASE':
      w.u8(idx(PHASES, e.from));
      w.u8(idx(PHASES, e.to));
      break;
    case 'ADVANTAGE':
      w.u8(idx(ADV_STATES, e.state));
      break;
    case 'PUNCH_LANDED':
      w.u8(e.puncher);
      w.u8(e.victim);
      break;
    case 'RAGDOLL':
      w.u8(e.entity);
      w.u8(idx(RAGDOLL_CAUSES, e.cause));
      break;
    case 'WHISTLE':
      w.u8(idx(WHISTLE_KINDS, e.kind));
      break;
    case 'POSSESSION':
      w.u8(entOr255(e.entity));
      w.u8(idx(POSSESSION_WHY, e.why)); // D119
      break;
    case 'SAVE':
      w.u8(e.keeper);
      w.u8(e.held ? 1 : 0);
      break;
    case 'EMOTE':
      w.u8(e.entity);
      w.u8(e.emote);
      break;
    case 'GK_PICKUP':
      w.u8(e.keeper);
      break;
    case 'GK_SMOTHER':
      w.u8(e.keeper);
      w.u8(e.victim);
      break;
    case 'RETURN': // D65c
      w.u8(e.entity);
      break;
  }
}

export function readSimEvent(r: BufReader): SimEvent {
  const t = look(EVENT_TYPES, r.u8());
  switch (t) {
    case 'GOAL':
      return { t, team: r.u8() as TeamIdx, scorer: r.u8(), assist: entOrNull(r.u8()), ownGoal: r.u8() === 1 };
    case 'SHOT':
      return { t, entity: r.u8(), onTarget: r.u8() === 1 };
    case 'MISS':
      return { t, entity: r.u8() };
    case 'FOUL':
      return { t, offender: r.u8(), victim: r.u8(), severity: r.f64(), outcome: look(FOUL_OUTCOMES, r.u8()), restart: look(RESTARTS, r.u8()), spot: { x: r.f64(), y: r.f64() } };
    case 'CARD':
      return { t, entity: r.u8(), card: cardOf(r.u8()), reason: look(CARD_REASONS, r.u8()) };
    case 'DIVE_JUDGED':
      return { t, entity: r.u8(), verdict: look(VERDICTS, r.u8()) };
    case 'OUT_OF_PLAY': {
      // read order is fixed: the spot's two f64s are consumed before the D122 taker byte
      const restart = look(RESTARTS, r.u8());
      const team = r.u8() as TeamIdx;
      const spot = { x: r.f64(), y: r.f64() };
      return { t, restart, team, spot, taker: entOrNull(r.u8()) };
    }
    case 'PHASE':
      return { t, from: look(PHASES, r.u8()), to: look(PHASES, r.u8()) };
    case 'ADVANTAGE':
      return { t, state: look(ADV_STATES, r.u8()) };
    case 'PUNCH_LANDED':
      return { t, puncher: r.u8(), victim: r.u8() };
    case 'RAGDOLL':
      return { t, entity: r.u8(), cause: look(RAGDOLL_CAUSES, r.u8()) };
    case 'WHISTLE':
      return { t, kind: look(WHISTLE_KINDS, r.u8()) };
    case 'POSSESSION':
      return { t, entity: entOrNull(r.u8()), why: look(POSSESSION_WHY, r.u8()) }; // D119
    case 'SAVE':
      return { t, keeper: r.u8(), held: r.u8() === 1 };
    case 'EMOTE':
      return { t, entity: r.u8(), emote: r.u8() };
    case 'GK_PICKUP':
      return { t, keeper: r.u8() };
    case 'GK_SMOTHER':
      return { t, keeper: r.u8(), victim: r.u8() };
    case 'RETURN': // D65c
      return { t, entity: r.u8() };
  }
}
